import { useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { listRoutes, createRoute, updateRoute, deleteRoute, type Route } from "../api/config";
import { listTrunks } from "../api/trunks";

const PAGE_SIZE = 50;

function RouteEditor({
  route,
  onClose,
  onSaved,
}: {
  route: Route | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [trunkId, setTrunkId] = useState(route?.trunk_id ?? 0);
  const [destinationId, setDestinationId] = useState(route?.destination_id ?? 0);
  const [priority, setPriority] = useState(route?.priority ?? 1);
  const [weight, setWeight] = useState(route?.weight ?? 1);

  const { data: trunks } = useQuery({
    queryKey: ["trunks", { per_page: 200 }],
    queryFn: () => listTrunks({ per_page: 200 }),
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        trunk_id: trunkId,
        destination_id: destinationId,
        priority,
        weight,
      };
      return route
        ? updateRoute(route.route_id, body)
        : createRoute(body).then(() => undefined);
    },
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-3">
        <h2 className="text-lg font-bold">{route ? "Edit" : "New"} route</h2>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Trunk</label>
          <select
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={trunkId}
            onChange={(e) => setTrunkId(Number(e.target.value))}
          >
            <option value={0}>Select a trunk…</option>
            {(trunks?.data ?? []).map((t) => (
              <option key={t.trunk_id} value={t.trunk_id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Destination ID</label>
          <input
            type="number"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={destinationId}
            onChange={(e) => setDestinationId(Number(e.target.value))}
          />
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Priority</label>
            <input
              type="number"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Weight</label>
            <input
              type="number"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={weight}
              onChange={(e) => setWeight(Number(e.target.value))}
            />
          </div>
        </div>
        {save.isError && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}
        <div className="flex gap-2 pt-2">
          <button
            disabled={!trunkId || !destinationId || save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function RoutesPage() {
  const [page, setPage] = useState(1);
  const qc = useQueryClient();
  const [editor, setEditor] = useState<Route | "new" | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["routes", page],
    queryFn: () => listRoutes(page, PAGE_SIZE),
    placeholderData: keepPreviousData,
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteRoute(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["routes"] }),
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / (data.per_page || PAGE_SIZE))) : 1;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Call Routes</h1>
        <button
          onClick={() => setEditor("new")}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
        >
          New route
        </button>
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-red-600">{(error as Error).message}</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
            {isFetching ? "Loading…" : `${data?.total.toLocaleString() ?? 0} routes`}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Route</th>
                <th className="px-4 py-2">Trunk</th>
                <th className="px-4 py-2">Destination</th>
                <th className="px-4 py-2 text-right">Priority</th>
                <th className="px-4 py-2 text-right">Weight</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-gray-500">
                    No routes.
                  </td>
                </tr>
              ) : (
                (data?.data ?? []).map((r) => (
                  <tr key={r.route_id}>
                    <td className="px-4 py-2 font-mono">#{r.route_id}</td>
                    <td className="px-4 py-2">#{r.trunk_id}</td>
                    <td className="px-4 py-2">#{r.destination_id}</td>
                    <td className="px-4 py-2 text-right">{r.priority}</td>
                    <td className="px-4 py-2 text-right">{r.weight}</td>
                    <td className="px-4 py-2 text-right space-x-3">
                      <button onClick={() => setEditor(r)} className="text-brand hover:underline">
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete route #${r.route_id}?`)) remove.mutate(r.route_id);
                        }}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 text-sm">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-gray-500">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {editor && (
        <RouteEditor
          route={editor === "new" ? null : editor}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            qc.invalidateQueries({ queryKey: ["routes"] });
          }}
        />
      )}
    </div>
  );
}
